import { Fragment } from 'react'
import { flexRender } from '@tanstack/react-table'
import clsx from 'clsx'

import { Loader } from '@/ui/Loader'
import { RadialBackground } from '@/ui/RadialBackground'
import { Table, TableRow, TableCell, TableHeader } from '@/ui/Table'
import { useUnfinishedLogsTable } from '../hooks/UnfinishedLogs/useUnfinishedLogsTable'
import { checkIsLinkedLog } from '../helpers/checkIsLinkedLog'
import { useCheckShouldHighlightRowAsUrgent } from '../helpers/useCheckShouldHighlightRowAsUrgent'
import { Log } from '@/types/logs'

import commonStyles from './CombinedLogs.module.scss'
import styles from './UnfinishedLogs.module.scss'

interface Props {
    displayTableHeader?: boolean
    displayEmptyPlaceholder?: boolean
    actionCallback?: (log: Log) => void
}

export const UnfinishedLogs = ({
    displayTableHeader = false,
    displayEmptyPlaceholder = false,
    actionCallback,
}: Props) => {
    const { table, hasNoRows, isLoading } = useUnfinishedLogsTable(actionCallback)

    const { checkShouldHighlightRowAsUrgent } = useCheckShouldHighlightRowAsUrgent()

    if (isLoading) {
        return <Loader />
    }

    if (hasNoRows && displayEmptyPlaceholder) {
        return (
            <div className={styles.emptyContainer}>
                <RadialBackground className={styles.background} variant="main">
                    <h4 className={styles.title}>No unfinished records</h4>
                    <p className={styles.description}>
                        Great work! Records that you need to complete will appear here.
                    </p>
                </RadialBackground>
            </div>
        )
    }

    return (
        <>
            <Table className={commonStyles.table}>
                {displayTableHeader ? (
                    <TableHeader table={table} />
                ) : (
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        style={{
                                            width: header.column.columnDef.meta?.width,
                                        }}
                                    ></th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                )}

                <tbody className={commonStyles.tableBody}>
                    {table.getRowModel().rows.map((row, rowIndex, array) => {
                        const isUrgent = checkShouldHighlightRowAsUrgent(row.original)

                        return (
                            <Fragment key={row.id}>
                                <TableRow
                                    row={row}
                                    key={row.id}
                                    className={clsx({
                                        [commonStyles.urgentRow]: isUrgent,
                                    })}
                                    isUrgent={isUrgent}
                                >
                                    {row.getVisibleCells().map((cell, cellIndex) => (
                                        <TableCell
                                            key={cell.id}
                                            alignCenter={cell.column.columnDef.meta?.alignCenter}
                                            className={clsx({
                                                [commonStyles.linkElement]:
                                                    !cellIndex && checkIsLinkedLog(row.original, rowIndex, array),
                                            })}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </Fragment>
                        )
                    })}
                </tbody>
            </Table>
        </>
    )
}
